import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ONBOARDING_SEEN_KEY = 'rodbt_onboarding_seen_v1';

type Slide = {
  id: string;
  title: string;
  subtitle: string;
};

const SLIDES: Slide[] = [
  {
    id: 'control-shell',
    title: 'Контроль — не всегда защита',
    subtitle: 'Иногда попытки всё держать под контролем изолируют нас от жизни и других людей',
  },
  {
    id: 'crack-in-shell',
    title: 'Открытость — это сила',
    subtitle:
      'Радикальная открытость учит принимать неопределённость и обратную связь без страха и защитной реакции',
  },
  {
    id: 'connection-signals',
    title: 'Близость начинается с сигналов',
    subtitle:
      'Мы учимся читать и посылать тонкие невербальные знаки — именно они создают настоящий контакт',
  },
  {
    id: 'inner-freedom',
    title: 'Гибкость вместо ригидности',
    subtitle: 'Цель — не потерять себя, а стать собой. Свободным, живым и открытым миру',
  },
];

function hasSeenOnboarding(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(ONBOARDING_SEEN_KEY) === '1';
}

function markOnboardingSeen() {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ONBOARDING_SEEN_KEY, '1');
}

export const FirstVisitOnboarding: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setOpen(!hasSeenOnboarding());
  }, []);

  const currentSlide = SLIDES[currentIndex] ?? SLIDES[0];
  const isLast = currentIndex === SLIDES.length - 1;

  const close = () => {
    markOnboardingSeen();
    setOpen(false);
  };
  const goNext = () => {
    setCurrentIndex((idx) => Math.min(idx + 1, SLIDES.length - 1));
  };

  const screenTitle = useMemo(
    () => `Экран ${currentIndex + 1} из ${SLIDES.length}`,
    [currentIndex],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80]"
      role="dialog"
      aria-modal="true"
      aria-label="Онбординг первого входа"
      data-testid="first-visit-onboarding"
    >
      <picture>
        <source
          media="(min-width: 1024px)"
          srcSet="/onboarding/shell-desktop.png 1x, /onboarding/shell-desktop@2x.png 2x"
        />
        <source
          media="(min-width: 640px)"
          srcSet="/onboarding/shell-tablet.png 1x, /onboarding/shell-tablet@2x.png 2x"
        />
        <img
          src="/onboarding/shell-mobile.png"
          srcSet="/onboarding/shell-mobile.png 1x, /onboarding/shell-mobile@2x.png 2x"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
      </picture>

      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/45 to-black/70" />

      <div className="relative flex h-full flex-col">
        <div className="flex items-center justify-end p-4 pt-[max(1rem,env(safe-area-inset-top))]">
          <button
            type="button"
            onClick={close}
            aria-label="Закрыть онбординг"
            data-testid="onboarding-close"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 items-end px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-8 sm:pb-8">
          <div
            className="w-full max-w-xl min-h-[300px] rounded-3xl border border-white/20 bg-black/30 p-5 text-white shadow-2xl backdrop-blur-md sm:min-h-[340px] sm:p-7 flex flex-col"
            data-testid="onboarding-card"
          >
            <div className="min-h-[150px] sm:min-h-[170px] flex-1">
              <p className="text-xs uppercase tracking-[0.16em] text-white/70">{screenTitle}</p>
              <h2 className="mt-2 text-2xl font-semibold leading-tight text-white sm:text-3xl">
                {currentSlide.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/90 sm:text-base">
                {currentSlide.subtitle}
              </p>
            </div>

            <div className="mt-auto pt-6 flex items-center justify-between gap-4" data-testid="onboarding-nav-row">
              <div
                className="flex items-center gap-2"
                role="tablist"
                aria-label="Навигация по слайдам онбординга"
              >
                {SLIDES.map((slide, idx) => {
                  const active = idx === currentIndex;
                  return (
                    <button
                      key={slide.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      aria-label={`Перейти к экрану ${idx + 1}`}
                      data-testid={`onboarding-dot-${idx + 1}`}
                      onClick={() => setCurrentIndex(idx)}
                      className={cn(
                        'h-2.5 rounded-full bg-white/55 transition-all duration-300 ease-out hover:bg-white/80',
                        active ? 'w-8 bg-white' : 'w-2.5',
                      )}
                    />
                  );
                })}
              </div>

              {isLast ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={close}
                  data-testid="onboarding-start"
                  className="border-white/45 bg-white/5 text-white hover:bg-white/20 hover:text-white"
                >
                  Приступить
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={goNext}
                  data-testid="onboarding-next"
                  className="border-white/45 bg-white/5 text-white hover:bg-white/20 hover:text-white"
                >
                  Далее
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

